import { useRef } from "react";

import { cn } from "~/lib/utils";

import type { KeyboardEvent, ReactNode } from "react";

/**
 * Chips de filtro de la landing pública.
 *
 * Por qué chips y no un `<Select>`: los cuatro selects que tenía esta página apilaban 274px en
 * mobile para operar sobre dos cursos, y arrastraban el look del fork — `SelectItem` pinta el ítem
 * bajo el cursor en celeste #e1eaf8 con texto azul marino #384995 (el `accent` del admin) sobre un
 * canvas crema, y ni el trigger ni el input tenían indicador de foco. Con chips el estado se ve sin
 * abrir nada y no hace falta tocar un primitivo que comparten el admin y seis pantallas internas.
 *
 * 44px de alto (`h-11`) porque es el mínimo táctil de las HIG. Los controles anteriores medían 42.
 *
 * ═══ Por qué el grupo es un componente y no tres chips sueltos ═══
 *
 * La versión anterior declaraba `role="radiogroup"` en el contenedor y `role="radio" aria-checked`
 * en cada chip, pero SIN el resto del contrato del patrón: los tres chips eran paradas de tab
 * independientes y las flechas no hacían nada. El lector de pantalla anunciaba "botón de opción,
 * 1 de 3" y prometía navegación con flechas que no existía (APG: un radiogroup tiene UNA sola
 * parada de tab y se recorre con flechas).
 *
 * De las dos salidas posibles — implementar el patrón entero, o bajar a `role="group"` con
 * `aria-pressed` — se eligió implementarlo entero, por dos razones:
 * 1. Son opciones EXCLUYENTES de un conjunto (una sola vale a la vez). `aria-pressed` describe
 *    botones que se prenden y apagan de a uno, y mentiría sobre la exclusividad.
 * 2. Con roving tabindex el grupo cuesta UNA parada de tab en vez de N. En una página cuyo problema
 *    declarado es la cantidad de cromo entre el fold y el primer producto eso importa: con el
 *    catálogo crecido son 2 paradas de tab en vez de 6 antes de llegar a la primera tarjeta.
 *
 * `role="radio"` y no `role="tab"`: son opciones excluyentes, pero no hay `tabpanel` detrás.
 */

export type FilterChipOption = {
  value: string;
  label: ReactNode;
};

type FilterChipGroupProps = {
  /** Va al `aria-label` del radiogroup: sin esto el lector anuncia un grupo sin nombre. */
  label: string;
  options: readonly FilterChipOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

/**
 * Cuando el chip vive en el riel horizontal de mobile, hay que traerlo a la ventana del riel a mano.
 * Dos razones, las dos medidas en `scripts/qa-courses-facets.mjs`:
 * 1. El `scrollIntoView` implícito del foco NO alcanza: con el chip PARCIALMENTE visible (por ejemplo
 *    después de tabular hasta el riel), Chrome no scrollea nada y el chip enfocado queda cortado.
 * 2. Los últimos 40px del riel están atenuados por el degradé de máscara, así que "visible" no es
 *    suficiente: el chip tiene que quedar afuera de esa franja o el anillo de foco se ve lavado
 *    (WCAG 2.4.11). De eso se encarga el `scroll-mr-10` de la clase del chip: `inline: "nearest"`
 *    respeta el `scroll-margin`, así que el mínimo scroll deja los 40px de aire.
 *
 * Es DOM, pero corre sólo en un handler de evento del usuario (foco / flecha), nunca en el cuerpo del
 * componente ni en un inicializador de estado: esta página se renderiza en el servidor.
 */
function revealInRail(chip: HTMLElement | null | undefined) {
  chip?.scrollIntoView({ block: "nearest", inline: "nearest" });
}

function chipClassName(active: boolean): string {
  return cn(
    "inline-flex h-11 shrink-0 scroll-mr-10 items-center rounded-lg border px-4 text-[14px] font-semibold transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-red focus-visible:ring-offset-2 focus-visible:ring-offset-landing-cream",
    active
      ? "border-landing-red bg-landing-red-50 text-landing-red"
      : "border-landing-border bg-landing-white text-landing-stone-700 hover:border-landing-red hover:text-landing-red",
  );
}

export function FilterChipGroup({
  label,
  options,
  value,
  onChange,
  className,
}: FilterChipGroupProps) {
  // Refs y no `document.querySelector`: esta página se renderiza en el SERVIDOR (framework de LLM
  // SEO), donde no hay DOM. Un ref es null en el server y se puebla al hidratar.
  const chips = useRef<Array<HTMLButtonElement | null>>([]);

  const selected = options.findIndex((option) => option.value === value);
  // Roving tabindex: si nada matchea (un `?category=` que ya no existe), la parada de tab queda en
  // el primer chip. Nunca cero paradas: el grupo tiene que ser alcanzable siempre.
  const tabStop = selected === -1 ? 0 : selected;

  /**
   * En un radiogroup la selección SIGUE al foco (APG): mover con flechas ya elige. Es lo que espera
   * un usuario de lector de pantalla, y acá no cuesta nada porque el filtrado es en cliente.
   */
  const moveTo = (index: number) => {
    const next = ((index % options.length) + options.length) % options.length;
    onChange(options[next].value);
    chips.current[next]?.focus();
    revealInRail(chips.current[next]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        moveTo(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        moveTo(index - 1);
        break;
      case "Home":
        event.preventDefault();
        moveTo(0);
        break;
      case "End":
        event.preventDefault();
        moveTo(options.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      // El default implícito de `radiogroup` es VERTICAL, y este grupo es un riel horizontal: sin
      // esto el lector de pantalla promete arriba/abajo cuando lo que hay es izquierda/derecha (las
      // cuatro flechas funcionan igual, ver `handleKeyDown`, pero el anuncio tiene que decir la
      // verdad). En desktop el grupo wrapea, y "horizontal" sigue siendo la lectura correcta: se
      // recorre por filas.
      aria-orientation="horizontal"
      // `shrink-0` + `flex-nowrap`: en mobile el grupo viaja dentro del riel horizontal de la barra
      // y no se puede comprimir, o los chips se aplastarían por debajo del mínimo táctil. En
      // desktop sí puede wrapear, que es donde hay ancho para hacerlo.
      className={cn("flex shrink-0 flex-nowrap items-center gap-2 sm:flex-wrap", className)}
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(el) => {
            chips.current[index] = el;
          }}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          tabIndex={index === tabStop ? 0 : -1}
          onClick={() => onChange(option.value)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          // Cubre la entrada al grupo con Tab (el chip con `tabIndex=0` puede estar fuera de la
          // ventana del riel); las flechas ya lo hacen en `moveTo`.
          onFocus={(event) => revealInRail(event.currentTarget)}
          className={chipClassName(option.value === value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
